import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import { timePattern } from "app/utils/timePattern";

import styles from "./ConfirmBar.module.scss";

interface Props {
  /** The chosen slot, as the booking state holds it. */
  time?: {
    startHour: number;
    endHour: number;
    timeZone?: string;
  } | null;
  optionTitle?: string;
  onConfirm: () => void;
  /**
   * CFTB-3426: drops the selection and with it the bar. The bar is sticky at the
   * foot of the screen, so it covers the last rows of the slot list — without a
   * way out, a slot underneath it could not be picked at all.
   */
  onDismiss?: () => void;
  disabled?: boolean;
}

/**
 * The bar that appears once a time is picked: the day, the option and the span
 * of the session, with the button that actually books it. Picking a slot only
 * selects it — this is the confirmation the frame puts at the foot of the screen.
 */
const ConfirmBar: React.FC<Props> = ({
  time,
  optionTitle,
  onConfirm,
  onDismiss,
  disabled = false,
}) => {
  const { t } = useTranslation();
  // CFTB-3431: matches the slots above it — see useCalendarPreferences.
  const { preferences } = useCalendarPreferences();
  const pattern = timePattern(preferences.timeFormat);

  const spacerRef = useRef<HTMLDivElement>(null);
  const startHour = time?.startHour ?? null;

  /**
   * Bring the foot of the page into view when a time is picked.
   *
   * The bar is tethered to the window, so it is already on screen — but the
   * slots it belongs to may not be, and a bar that appears over a page the
   * athlete cannot see the end of reads as though something is still below it.
   * Scrolling to the spacer — which sits exactly where the bar hovers — puts the
   * choice and the confirmation on screen together.
   *
   * `scrollIntoView` walks every scrollable ancestor, so this works both on the
   * public page, which scrolls the document, and in the app, which scrolls its
   * own pane.
   *
   * Before the early return: hooks run on every render or none.
   */
  useEffect(() => {
    if (!startHour) {
      return;
    }
    spacerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [startHour]);

  if (!time?.startHour) {
    return null;
  }

  const zone = time.timeZone;
  const start = DateTime.fromMillis(time.startHour, zone ? { zone } : {});
  const end = time.endHour
    ? DateTime.fromMillis(time.endHour, zone ? { zone } : {})
    : null;

  if (!start.isValid) {
    return null;
  }

  /**
   * CFTB-3477: the bar hangs from the body, not from wherever it was rendered.
   *
   * `position: fixed` is measured against the viewport only while no ancestor has
   * a transform, a filter or a will-change; any of those makes that ancestor the
   * containing block instead. The app's `.child_wrapper` carries
   * `transform: translate3d(0, 0, 0)` — a layer-promotion hack — so inside the app
   * the bar was fixed to a 1140px box that scrolls with the page: measured on the
   * deployed page, it sat 54px above the foot at one scroll position and 160px
   * *below* the window at another, and moved as the page moved.
   *
   * Portalled to the body, it is the window's again on every screen. The spacer
   * stays where it is — its job is to hold the page's last row clear, which only
   * works in the flow.
   */
  const bar = (
    <div className={styles.wrapper}>
      <div className={styles.bar} data-testid="booking-confirm-bar">
        <div className={styles.summary}>
          <div className={styles.headline}>
            <span className={styles.day}>
              {start.toFormat("cccc, LLLL d")}
            </span>
            {optionTitle && (
              <>
                <span className={styles.separator}>•</span>
                {/*
                  CFTB-3463: the one place the line may break. JSX puts no space
                  between these, so without it the option has nowhere to go and
                  overflows the bar on a phone; with it the bullet stays with the
                  date and the option takes the next line, as the mock draws it.
                */}
                <wbr />
                <span className={styles.option}>{optionTitle}</span>
              </>
            )}
          </div>
          <div className={styles.time}>
            {start.toFormat(pattern)}
            {end?.isValid ? ` - ${end.toFormat(pattern)}` : ""}
          </div>
        </div>

        <button
          type="button"
          className={styles.confirm}
          onClick={onConfirm}
          disabled={disabled}
          data-testid="booking-confirm"
        >
          {t("Confirm")}
        </button>

        {onDismiss && (
          <button
            type="button"
            className={styles.dismiss}
            onClick={onDismiss}
            aria-label={t("Cancel selection")}
            title={t("Cancel selection")}
            data-testid="booking-confirm-dismiss"
          >
            {/* `ico-x`, not `ico-close`: the icon font has no such glyph, and the
                class it does not define renders an empty button. */}
            <i className="ico ico-x" />
          </button>
        )}
        </div>
    </div>
  );

  return (
    <>
      {/* Keeps the page's last row clear of a bar that no longer takes space, and
          is what the page scrolls to when a time is picked. */}
      <div ref={spacerRef} className={styles.spacer} aria-hidden />
      {createPortal(bar, document.body)}
    </>
  );
};

export default ConfirmBar;
